import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import ImageComparisonSlider from "./ImageComparisonSlider";

const ShowcasePage = () => {
  const [activeTab, setActiveTab] = useState("clinic");
  const [currentCase, setCurrentCase] = useState(0);

  const videoRefs = useRef({});

  const handlePlay = (currentId) => {
    Object.values(videoRefs.current).forEach((videoEl) => {
      if (!videoEl) return;
      if (videoEl.dataset.id === String(currentId)) return;
      videoEl.pause();
    });
  };

  const beforeAfterCases = useMemo(
    () => [
      {
        id: 1,
        before: "/Images/Jesus Gomez separate.png",
        after: "/Images/Jesus Gomez afterSeparate.png",
      },
      {
        id: 2,
        before: "/Images/Maria Luna before Separate.png",
        after: "/Images/Maria Luna after separate2.png",
      },
      {
        id: 3,
        before: "/Images/tony jordan before separate.png",
        after: "/Images/tony jordan after separate.png",
      },
      {
        id: 4,
        before: "/Images/David Travis before separate.png",
        after: "/Images/David Travis  after separate .png",
      },
    ],
    []
  );

  const beforeAfterVideoCases = useMemo(
    () => [
      { id: 1, title: "Smile Makeover · Case 1", src: "/Images/1stBefore.mp4" },
      { id: 2, title: "Smile Makeover · Case 2", src: "/Images/2ndBefore.mp4" },
      { id: 3, title: "Smile Makeover · Case 3", src: "/Images/3rd before.mp4" },
      { id: 4, title: "Smile Makeover · Case 4", src: "/Images/4thBefore.mp4" },
    ],
    []
  );

  const handleNext = () => {
    setCurrentCase((prev) => (prev + 1) % beforeAfterCases.length);
  };

  const handlePrev = () => {
    setCurrentCase((prev) =>
      prev === 0 ? beforeAfterCases.length - 1 : prev - 1
    );
  };

  // ✅ Put your long testimonials + doctor images here
  // Make sure these image paths exist in /public/Images/...
  const testimonials = useMemo(
    () => [
      {
        name: "Dr. Vivek Solanki",
        rating: 5,
        image: "/Images/vivekcropped.png",
        review:
          "My name is Dr. Vivek Solanki, and I am the owner of several multi-specialty dental practices in the greater Tampa area and surrounding communities.\n\nI have had the privilege of working with Ace Dental Lab for the past year, and I cannot say enough about their exceptional quality, attention to detail, communication, and guidance throughout the entire full-arch process.\n\nWhile many labs may assist solely with design or milling of zirconia finals, what truly sets Ace Dental Lab apart is that they function as a true extension of our practice. Their team provides invaluable support on the equipment side, assists with thoughtful case design, and offers strategic guidance to help ensure predictable and successful outcomes.\n\nThey also present multiple options for both provisionals and final restorations, allowing us to deliver the best possible results for our patients.\n\nI am extremely grateful for the continued support from Ace Dental Lab and would like to thank their entire team for the outstanding care and dedication they provide to our patients and our team throughout Florida.",
      },
      {
        name: "Dr. Kelley Shimada",
        rating: 5,
        image: "/Images/girl.jpeg",
        review:
          "Ace Dental Lab has been a huge asset to our full arch workflow. The team has been incredibly easy to work with and communication is open and responsive.\n\nOur patients are always satisfied with the designs and arches we receive from Ace Dental Lab. The lab team is also very flexible regarding turnaround times and due dates.\n\nI definitely recommend Ace Dental Lab to any office looking to increase and improve their full arch productivity. My only regret is that we didn’t find them sooner!",
      },
      {
        name: "Dr. Azam Saeed",
        rating: 5,
        image: "/Images/azamsaeed.jpeg",
        review:
          "As Ace Dental Center evolved into a full-arch–focused practice, the need for lab support that could keep pace with surgical timelines, meet tight deadlines, and stay aligned with the patient experience became clear. Ace Dental Lab was established to meet that demand, providing efficient, reliable full-arch restorations designed to support fast-paced clinical workflows.\n\nAfter delivering thousands of All-on-X arches for Ace Dental Center, Ace Dental Lab has developed a deep understanding of the challenges that can arise from both a laboratory and clinical standpoint. That dual perspective allows the lab to support doctors across the country with practical, predictable solutions that simplify workflows, reduce unnecessary try-ins and occlusal adjustments, and improve the overall doctor–patient experience.\n\nThe goal is to allow clinicians to focus on what they do best—surgery—while maximizing chair time efficiency, all backed by real-world experience in both the clinic and the lab.",
      },
    ],
    []
  );

  // ---------- ⭐ Helpers ----------
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? "text-yellow-400 fill-current" : "text-white/30"
        }`}
      />
    ));
  };

  // ---------- ✅ Testimonial Carousel Logic ----------
  const [tIndex, setTIndex] = useState(0);
  const [tPaused, setTPaused] = useState(false);

  useEffect(() => {
    if (tPaused) return;
    const id = setInterval(() => {
      setTIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(id);
  }, [tPaused, testimonials.length]);

  const goPrevTestimonial = () =>
    setTIndex((p) => (p - 1 + testimonials.length) % testimonials.length);

  const goNextTestimonial = () =>
    setTIndex((p) => (p + 1) % testimonials.length);

  const activeT = testimonials[tIndex];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="min-h-[70vh] bg-black from-slate-900 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h6 className="text-white font-medium mb-4 tracking-wide uppercase mt-10">
            Our Work in Smiles
          </h6>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8">
            Explore our{" "}
            <span className="bg-gradient-to-r text-white to-cyan-300 bg-clip-text text-transparent">
              Dental Transformations
            </span>
          </h1>
        </div>
      </section>

      {/* Before & After Image Grid */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Before &amp; After Transformations
            </h2>
            <p className="text-lg text-white max-w-3xl mx-auto">
              Real cases demonstrating how restorative and prosthetic work can
              completely transform a smile.
            </p>
          </div>

          <div className="grid gap-10 md:grid-cols-2">
            {beforeAfterCases.map((item, index) => (
              <div key={item.id ?? index} className="flex flex-col items-center">
                <ImageComparisonSlider before={item.before} after={item.after} />
                <p className="mt-4 text-sm text-white text-center">
                  Case {index + 1}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before / After Video Reels Section */}
      <section className="py-10 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Before &amp; After Smile Reels
            </h2>
            <p className="text-lg text-white max-w-3xl mx-auto">
              Watch real smile transformations — each video shows the journey
              from the original condition to the final result.
            </p>
          </div>

          {/* Desktop / Tablet Grid */}
          <div className="hidden md:grid gap-8 md:grid-cols-2 lg:grid-cols-2">
            {beforeAfterVideoCases.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl transition-all duration-200 border border-blue-100 flex flex-col"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-blue-600">
                    {item.id}
                  </span>
                </div>

                <div className="relative w-full overflow-hidden rounded-2xl bg-black aspect-[3/4]">
                  <video
                    data-id={`desktop-${item.id}`}
                    ref={(el) => {
                      if (el) videoRefs.current[`desktop-${item.id}`] = el;
                      else delete videoRefs.current[`desktop-${item.id}`];
                    }}
                    src={item.src}
                    controls
                    controlsList="nodownload"
                    playsInline
                    onPlaying={() => handlePlay(`desktop-${item.id}`)}
                    className="w-full h-full object-cover"
                  />
                </div>

                <p className="mt-4 text-sm font-medium text-gray-800 text-center">
                  {item.title}
                </p>
              </div>
            ))}
          </div>

          {/* Mobile horizontal scroll */}
          <div className="md:hidden">
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
              {beforeAfterVideoCases.map((item) => (
                <div
                  key={item.id}
                  className="min-w-[80%] snap-center bg-white rounded-2xl p-4 shadow-lg border border-blue-100"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-blue-600">
                      Case {item.id}
                    </span>
                    <span className="text-xs text-gray-400 uppercase tracking-wide">
                      Before / After
                    </span>
                  </div>

                  <div className="relative w-full overflow-hidden rounded-2xl bg-black aspect-[9/16]">
                    <video
                      data-id={`mobile-${item.id}`}
                      ref={(el) => {
                        if (el) videoRefs.current[`mobile-${item.id}`] = el;
                        else delete videoRefs.current[`mobile-${item.id}`];
                      }}
                      src={item.src}
                      controls
                      controlsList="nodownload"
                      playsInline
                      onPlaying={() => handlePlay(`mobile-${item.id}`)}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <p className="mt-4 text-sm font-medium text-gray-800 text-center">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Text Section */}
      <section className="py-10 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Makes Ace Dental Lab Different?
            </h2>
            <p className="text-lg text-white max-w-3xl mx-auto leading-relaxed">
              More than just a dental lab — we focus on innovation, precision,
              and long-lasting results.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
              <h3 className="text-xl font-semibold text-black mb-4">
                Precision & Accuracy
              </h3>
              <p className="text-black leading-relaxed">
                Every restoration is crafted with extreme attention to detail,
                ensuring comfort, durability, and a perfect fit for every
                patient.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
              <h3 className="text-xl font-semibold text-black mb-4">
                Dedicated Team
              </h3>
              <p className="text-black leading-relaxed">
                Our experienced technicians work closely with clinics to provide
                fully customized solutions that meet clinical standards and
                patient needs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
              <h3 className="text-xl font-semibold text-black mb-4">
                Reliable Turnaround
              </h3>
              <p className="text-black leading-relaxed">
                No delays, no guesswork — just timely restorations that fit your
                workflow perfectly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Text Section */}
      <section className="py-14 bg-black border-black-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Craft, Our Commitment
            </h2>
            <p className="text-lg text-white max-w-3xl mx-auto leading-relaxed">
              At Ace Dental Lab, every smile we help create reflects our
              dedication. We focus on precision, consistency, and innovation to
              deliver work that dentists trust and patients appreciate.
            </p>
          </div>

          <div className="space-y-12">
            <div className="bg-slate-50 p-10 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
              <h3 className="text-2xl font-semibold text-black mb-4">
                Quality Above Everything
              </h3>
              <p className="text-black leading-relaxed">
                Every restoration, every detail, and every finish is handled
                with extreme care. Our work isn’t rushed — it’s refined. We use
                modern techniques and reliable materials to ensure long-lasting
                and naturally aesthetic outcomes for patients.
              </p>
            </div>

            <div className="bg-slate-50 p-10 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
              <h3 className="text-2xl font-semibold text-black mb-4">
                A Team You Can Rely On
              </h3>
              <p className="text-black leading-relaxed">
                Behind every case is a skilled team of technicians who
                understand the importance of precision. We collaborate openly
                with clinics, supporting dentists with clear communication and
                consistent results.
              </p>
            </div>

            <div className="bg-slate-50 p-10 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
              <h3 className="text-2xl font-semibold text-black mb-4">
                Designed to Support Your Practice
              </h3>
              <p className="text-black leading-relaxed">
                Whether it’s digital workflows, ceramic artistry, or implant
                restorations — our goal is to help your clinic deliver better
                experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ Testimonials Carousel (REPLACED SECTION) */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Doctors Testimonials
            </h2>
            
          </div>

          <div
            className="relative rounded-3xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 shadow-2xl overflow-hidden"
            onMouseEnter={() => setTPaused(true)}
            onMouseLeave={() => setTPaused(false)}
          >
            <div className="p-6 sm:p-10">
              <div className="grid md:grid-cols-[280px_1fr] gap-8 items-start">
                {/* Bigger Doctor Image */}
                <div className="flex md:flex-col items-center md:items-start gap-4">
                  <img
                    src={activeT.image}
                    alt={activeT.name}
                    className="w-28 h-28 sm:w-32 sm:h-32 md:w-64 md:h-64 rounded-2xl object-cover border border-white/15 shadow-lg"
                  />

                  <div className="md:mt-3 text-center md:text-left">
                    <p className="text-white text-lg font-semibold">
                      {activeT.name}
                    </p>

                    <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                      <div className="flex">{renderStars(activeT.rating)}</div>
                      <span className="text-white/70 text-sm">
                        {activeT.rating}/5
                      </span>
                    </div>
                  </div>
                </div>

                {/* Review */}
                <div>
                  <p className="text-white/85 leading-relaxed text-base sm:text-lg whitespace-pre-line italic">
                    “{activeT.review}”
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                {/* Dots */}
                <div className="flex items-center gap-2">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setTIndex(i)}
                      className={`h-2.5 rounded-full transition-all ${
                        i === tIndex
                          ? "w-8 bg-white"
                          : "w-2.5 bg-white/30 hover:bg-white/50"
                      }`}
                      aria-label={`Go to testimonial ${i + 1}`}
                    />
                  ))}
                </div>

                {/* Arrows */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={goPrevTestimonial}
                    className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/15 border border-white/10 transition"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={goNextTestimonial}
                    className="px-4 py-2 rounded-xl bg-white text-black hover:brightness-95 transition font-semibold"
                  >
                    Next →
                  </button>
                </div>
              </div>

              <p className="mt-4 text-center text-white/40 text-xs">
                
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcasePage;
